import { useEffect } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { showError } from '@/lib/alert'
import { usePageTitle } from '@/hooks/usePageTitle'
import { useAuth } from '@/auth/AuthContext'
import { Preloader } from '@/components/Preloader'
import { DescriptionBlock } from '@/features/vacancy/DescriptionBlock'
import { capitalizedText } from '@/lib/format'
import type { VacancyRow } from '@/features/vacancy/types'
import './vacancy.css'

/**
 * Port of Views/Vacancy/Info.cshtml.
 *
 * The vacancy id in the URL is encoded, so it is decoded server-side by
 * GET vacancy/info-context; a bad or expired link answers with `redirectTo`,
 * reproducing `HandleDecodeError`'s bounce back to the listing.
 *
 * Company branding here matches on a SUBSTRING, case-insensitively
 * ("Tower Bersama", "Infrastruktur Digital", "Permata Karya Perdana"), where the
 * listing card matched the full name exactly. The two rules disagree — a company
 * named "PT Tower Bersama Infrastructure" without ", Tbk" gets a logo here and
 * the default on the card — but both are transcribed as they were, because
 * either could be the one the data actually relies on.
 *
 * PRESENTATION CHANGE, deliberate and not a like-for-like port:
 *
 * Info.cshtml displayed every value through `<input disabled readOnly>` and the
 * two long descriptions through `<textarea disabled rows="10">`. Nothing on the
 * page was ever editable, so the form controls were being used purely as boxes
 * to print text in — and `:disabled` paints Bootstrap's #e9ecef fill with muted
 * grey text, which is the browser's way of saying "you cannot use this".
 * A job advert rendered in the styling reserved for dead controls reads as
 * broken, and the ten-row textareas hid the rest of the advert behind an inner
 * scrollbar.
 *
 * Values are now plain text, the descriptions render as the lists they were
 * already written as (see DescriptionBlock), and "Apply Now" moved below the
 * advert it belongs to.
 *
 * The card is built from plain divs, NOT header/footer/section elements.
 * landing.css styles those bare tags for page-level use: `header` gets
 * `display: flex; flex-direction: column; align-items: center` and `section`
 * gets `padding: 100px 0`. Used inside the card that silently collapsed the
 * four-fact grid from the full 854px width to a 176px centred column — the
 * element selector wins wherever this file does not name the property.
 *
 * Job type and location are shown alongside career and deadline. The listing
 * card has always displayed `JobContractsName` and `ProvinceName`
 * (VacancyCard.tsx), so opening a vacancy used to LOSE two facts the user could
 * already see; both are populated on all 100 live vacancies.
 */
const BRANDING = [
  { match: 'tower bersama', logo: '/lib/images/logo.png', website: 'www.tower-bersama.com' },
  {
    match: 'infrastruktur digital',
    logo: '/lib/images/ideanet_logo.png',
    website: 'www.ideanet.net.id',
  },
  {
    match: 'permata karya perdana',
    logo: '/lib/images/LOGO-PKP-2023.png',
    website: 'www.ptvti.co.id',
  },
]

/**
 * The four facts in the header band. Icons match the listing card's own choices
 * (VacancyCard.tsx) so a vacancy reads the same on both screens, and every name
 * here is verified present in the bundled tabler-icons.min.css.
 */
const META: {
  label: string
  icon: string
  read: (vacancy: VacancyRow) => string
}[] = [
  { label: 'Career', icon: 'ti ti-user', read: (v) => v.CareerName ?? '' },
  { label: 'Job Type', icon: 'ti ti-id', read: (v) => v.JobContractsName ?? '' },
  {
    label: 'Location',
    icon: 'ti ti-map-pin',
    // The upstream stores provinces upper-cased ("DKI JAKARTA"); the listing
    // lower-cases then title-cases them, and this matches it.
    read: (v) => capitalizedText((v.ProvinceName ?? '').toLowerCase()),
  },
  { label: 'Deadline', icon: 'ti ti-calendar-event', read: (v) => formatDeadline(v.EndVacancy) },
]

interface InfoContext {
  success: boolean
  message?: string
  redirectTo?: string
  trxVacancyID?: number
  encodedTrxVacancyID?: string
  vacancy?: VacancyRow | null
}

export function VacancyInfo(): JSX.Element {
  usePageTitle('Application Form')
  const { encodedTrxVacancyID } = useParams<{ encodedTrxVacancyID: string }>()
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()

  const { data, isLoading } = useQuery({
    queryKey: ['vacancy', 'info', encodedTrxVacancyID],
    queryFn: () =>
      api.get<InfoContext>(
        `/api/ecareer/vacancy/info-context/${encodeURIComponent(encodedTrxVacancyID ?? '')}`,
      ),
    retry: false,
  })

  useEffect(() => {
    if (!data?.redirectTo) return
    void (async () => {
      if (data.message) await showError(data.message)
      navigate(data.redirectTo ?? '/Vacancy')
    })()
  }, [data, navigate])

  if (isLoading) return <Preloader />

  const vacancy = data?.vacancy
  if (!vacancy) return <Preloader />

  const companyName = vacancy.CompanyName ?? ''
  const branding = BRANDING.find((entry) => companyName.toLowerCase().includes(entry.match))

  return (
    <section className="bg-white">
      <div className="container">
        <div className="row justify-content-center text-center">
          <div className="col-md-8 col-xl-6">
            <h2 className="section-title">Job Description</h2>
            <p className="mt-lg-4 mt-2 mb-4 mb-md-5">
              Detail for the position of <span>{vacancy.PositionName}</span>
            </p>
          </div>
        </div>

        <div className="row justify-content-center">
          <div className="col-12 col-md-10 col-xl-8">
            <article className="vacancy-info">
              <div className="vacancy-info__masthead">
                <div className="vacancy-info__identity">
                  {branding && (
                    <span className="vacancy-info__logo-tile">
                      <img src={branding.logo} alt={companyName} />
                    </span>
                  )}
                  <div>
                    <h3 className="vacancy-info__position">{vacancy.PositionName}</h3>
                    <p className="vacancy-info__company">
                      {companyName}
                      {branding && (
                        // Deliberately not a link: the original <a> had no
                        // href, so this has always been plain text.
                        <span className="vacancy-info__website">{branding.website}</span>
                      )}
                    </p>
                  </div>
                </div>

                <dl className="vacancy-info__meta">
                  {META.map((fact) => (
                    <div key={fact.label} className="vacancy-info__meta-item">
                      <span className="vacancy-info__meta-icon">
                        <i className={fact.icon} aria-hidden="true" />
                      </span>
                      <div>
                        <dt>{fact.label}</dt>
                        <dd>{fact.read(vacancy) || '—'}</dd>
                      </div>
                    </div>
                  ))}
                </dl>
              </div>

              <div className="row g-3 vacancy-info__sections">
                <div className="col-12 col-lg-6">
                  <div className="vacancy-info__panel">
                    <h4 className="vacancy-info__heading">
                      <span className="vacancy-info__heading-icon">
                        <i className="ti ti-clipboard-list" aria-hidden="true" />
                      </span>
                      Task &amp; Responsibilities
                    </h4>
                    <DescriptionBlock text={vacancy.ResponsibilityDescription} marker="arrow" />
                  </div>
                </div>

                <div className="col-12 col-lg-6">
                  <div className="vacancy-info__panel">
                    <h4 className="vacancy-info__heading">
                      <span className="vacancy-info__heading-icon vacancy-info__heading-icon--check">
                        <i className="ti ti-user-check" aria-hidden="true" />
                      </span>
                      Qualification
                    </h4>
                    <DescriptionBlock text={vacancy.QualificationDescription} marker="check" />
                  </div>
                </div>
              </div>

              <div className="vacancy-info__actions">
                <div className="vacancy-info__actions-note">
                  <i className="ti ti-calendar-event" aria-hidden="true" />
                  Applications close on {formatDeadline(vacancy.EndVacancy) || 'a date to be announced'}
                </div>
                <div className="vacancy-info__actions-buttons">
                  <Link to="/Vacancy" className="btn btn-outline-primary">
                    Back to vacancies
                  </Link>
                  {isAuthenticated ? (
                    <Link
                      to={`/Vacancy/Apply/${vacancy.trxVacancyID}`}
                      className="btn btn-primary vacancy-info__apply"
                    >
                      Apply Now
                    </Link>
                  ) : (
                    <Link
                      to="/Account/Login"
                      className="btn btn-primary btnApplyNow vacancy-info__apply"
                      data-vacancy-id={vacancy.trxVacancyID}
                    >
                      Apply Now
                    </Link>
                  )}
                </div>
              </div>
            </article>
          </div>
        </div>
      </div>
    </section>
  )
}

/**
 * `@Model.EndVacancy.ToString("dd MMMM yyyy")` — invariant month names, which is
 * what a C# DateTime formats to under the server's default culture here.
 * `toLocaleDateString('en-GB')` produces the same "02 January 2024" shape.
 */
function formatDeadline(value: string | null | undefined): string {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })
}
